import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Chip,
  Button,
  IconButton,
  Alert
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import GitHubIcon from '@mui/icons-material/GitHub';
import { FaBitbucket, FaGitlab, FaJira, FaStar, FaTicketAlt } from 'react-icons/fa';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import KeyIcon from '@mui/icons-material/VpnKey';
import CommentIcon from '@mui/icons-material/Comment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import BookIcon from '@mui/icons-material/Book';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { BookOpen, ExternalLink, Target, Zap } from 'lucide-react';

const instructions = {
  GitHub: {
    title: 'GitHub Personal Access Token',
    steps: [
      'Go to **GitHub Settings** → **Developer settings** → **Personal access tokens** → **Tokens (classic)**',
      'Click **"Generate new token (classic)"**',
      'Set expiration to **1 year** or **No expiration**',
      'Select the following scopes:\n• **repo** (Full control of private repositories)\n• **user** (Update ALL user data)\n• **admin:repo_hook** (Full control of repository hooks)',
      'Click **"Generate token"**',
      'Copy the token immediately and paste it into the GitHub Token field below'
    ],
    inputLabel: 'GitHub Token:',
    inputPlaceholder: 'ghp_xxxxxxxxxxxxxxxxxxxx',
    alerts: [
      { type: 'warning', title: 'Important', message: 'These scopes are mandatory for proper functionality.' },
      { type: 'info', title: 'Security Note', message: 'You won\'t see the token again after creation. Store it securely and never share it.' }
    ]
  },
  GitLab: {
    title: 'GitLab Personal Access Token',
    steps: [
      'Go to GitLab Personal Access Tokens',
      'Click **Create personal access token**',
      'Select scopes: **api** (or at least read_api + write_repository)',
      'Copy token and paste into GitLab Token field'
    ],
    inputLabel: 'GitLab Token:',
    inputPlaceholder: 'glpat-xxxxxxxxxxxxxxxxxxxx',
    alerts: [
      { type: 'warning', title: 'Important', message: 'These permissions are mandatory.' },
      { type: 'info', title: 'Note', message: 'Personal access tokens are safer than your main password. Store securely.' }
    ]
  },
  Bitbucket: {
    title: 'Bitbucket App Password',
    steps: [
      'Go to **Bitbucket App Passwords**',
      'Click **Create app password**',
      'Set permissions: **Repository (Read & Write), Pull Requests (Read & Write), Webhooks (Read & Write)**',
      'Copy your username + generated password → enter in setup form'
    ],
    inputLabel: 'Bitbucket Username:',
    inputPlaceholder: 'your-username',
    alerts: [
      { type: 'warning', title: 'Important', message: 'These permissions are mandatory.' },
      { type: 'info', title: 'Note', message: 'App passwords are safer than your main password. Store securely.' }
    ]
  },
  Azure: {
    title: 'Azure DevOps Personal Access Token',
    steps: [
      'Go to **Azure DevOps organization** → **Profile** → **Security**',
      'Click **Personal access tokens** → **New Token**',
      '**Name:** **AI Code Review Tool**, **Organization:** **All accessible orgs**',
      '**Expiration:** **1 year recommended**',
      '**Scopes (Custom): Code (Read & Write), Work Items (Read & Write), Pull Request Threads (Read & Write)**',
      'Copy + paste token into Azure DevOps Token field'
    ],
    inputLabel: 'Azure DevOps Token:',
    inputPlaceholder: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    alerts: [
      { type: 'warning', title: 'Important', message: 'These scopes are mandatory.' },
      { type: 'info', title: 'Note', message: 'You won\'t see the token again after creation. Store securely.' }
    ]
  },
  OpenAI: {
    title: 'OpenAI API Key',
    steps: [
      'Go to **OpenAI Platform** → **API Keys**',
      'Click **Create new secret key**',
      'Set a name for your key (e.g., **Sniffr.ai Code Review**)',
      'Copy the API key immediately and paste it into the OpenAI API Key field below'
    ],
    inputLabel: 'OpenAI API Key:',
    inputPlaceholder: 'sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    alerts: [
      { type: 'warning', title: 'Important', message: 'OpenAI charges per token usage. Monitor your usage through your OpenAI dashboard.' },
      { type: 'info', title: 'Security Note', message: 'Keep your API key secure. Never share it or commit it to version control.' }
    ]
  },
  Gemini: {
    title: 'Google Gemini API Key',
    steps: [
      'Go to **Google AI Studio** → **Get API key**',
      'Sign in with your Google account',
      'Click **Create API key in new project** or select existing project',
      'Copy the generated API key and paste it into the Gemini API Key field below'
    ],
    inputLabel: 'Gemini API Key:',
    inputPlaceholder: 'AlzaSyxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    alerts: [
      { type: 'info', title: 'Free Tier', message: 'Gemini offers a generous free tier with rate limits. Perfect for getting started.' },
      { type: 'info', title: 'Security Note', message: 'Keep your API key secure. Never share it or commit it to version control.' }
    ]
  },
  Claude: {
    title: 'Anthropic Claude API Key',
    steps: [
      'Go to **Anthropic Console** → **API Keys**',
      'Sign in with your Anthropic account',
      'Click **Create Key**',
      'Set a name for your key (e.g., **Sniffr.ai Code Review**)',
      'Copy the API key immediately and paste it into the Claude API Key field below'
    ],
    inputLabel: 'Claude API Key:',
    inputPlaceholder: 'sk-ant-api03-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    alerts: [
      { type: 'warning', title: 'Important', message: 'Claude charges per token usage. Monitor your usage through your Anthropic dashboard.' },
      { type: 'info', title: 'Security Note', message: 'Keep your API key secure. Never share it or commit it to version control.' }
    ]
  },
  JIRA: {
    title: 'JIRA Integration',
    steps: [
      'Go to **Atlassian Account Settings** → **Security** → **Create and manage API tokens**',
      'Click **Create API token**',
      'Set a label (e.g., **Sniffr.ai Integration**)',
      'Copy the generated token immediately',
      'Enter your Atlassian email address and JIRA domain URL below'
    ],
    inputLabel: 'JIRA API Token:',
    inputPlaceholder: 'ATATT3xFfGF0xxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    alerts: [
      { type: 'warning', title: 'Mandatory', message: 'read:jira-work (view issues, projects, boards). Optional: write:jira-work (create/update issues).' },
      { type: 'info', title: 'Security Recommendation', message: 'Use a dedicated service account for API integrations to maintain security and access control.' },
      { type: 'info', title: 'Integration Note', message: 'JIRA integration enables automatic ticket creation and status updates based on code review findings.' }
    ]
  },
  Trello: {
    title: 'Trello Integration',
    steps: [
      'Go to the **Trello Power-Up Admin Portal** at https://trello.com/power-ups/admin',
      'Create a new Power-Up or select an existing one',
      'Navigate to the **"API key"** page in the Power-Up settings',
      'Copy your **API Key** from the page (each Power-Up has a unique API key)',
      'To generate a token, visit **Trello App Key** at https://trello.com/app-key and click the **"Token"** link',
      'Authorize the application if prompted',
      'Copy the generated **Token** (displayed on the page)',
      'Paste both the API Key and Token into the Trello configuration fields below'
    ],
    inputLabel: 'Trello API Key:',
    inputPlaceholder: 'your-api-key',
    alerts: [
      { type: 'warning', title: 'Security Note', message: 'Keep your token secure. It provides full access to your Trello account. Each Power-Up can have a unique API key for better management.' },
      { type: 'info', title: 'Integration Note', message: 'Trello integration enables card tracking, cycle time calculation, and requirement validation. Use Trello card IDs or URLs in your PR titles to link them (e.g., [TRELLO-abc12345] or https://trello.com/c/abc12345).' }
    ]
  },
  'Zoho Sprints': {
    title: 'Zoho Sprints Integration',
    steps: [
      'Go to **Zoho API Console** (api-console.zoho.in for India, .com for US, .eu for EU) → **Add Client** → **Self Client**',
      'Note down the **Client ID** and **Client Secret** from the client page',
      'In the Self Client page, click **Generate Code** tab. Enter scope: **ZohoSprints.teams.READ,ZohoSprints.projects.READ,ZohoSprints.items.READ,ZohoSprints.sprints.READ** → select your Zoho Sprints portal → click **Create**',
      'Copy the generated **Grant Token** (expires in a few minutes — paste it promptly)',
      'Paste **Client ID**, **Client Secret**, **Grant Token**, and your **Domain** (zoho.in / zoho.com / zoho.eu) into the project form',
      'Click **Fetch Teams & Projects** — the system will exchange your grant token and load your teams and projects automatically',
      'Select your **Team** and **Project** from the dropdowns, then save'
    ],
    inputLabel: 'Zoho Client ID:',
    inputPlaceholder: '1000.XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX',
    alerts: [
      { type: 'warning', title: 'Grant Token Expiry', message: 'The grant token can only be used once and expires within minutes. Generate it right before clicking "Fetch Teams & Projects". The system automatically exchanges it for long-lived tokens.' },
      { type: 'info', title: 'PR Title Format', message: 'Reference Zoho Sprints items in your PR title using: [ZOHO-3], P1#I-3, or QDE-I3 (where the number is the item number from your board).' }
    ]
  }
};

const getProviderInstructions = (provider) => {
  return instructions[provider] || instructions.GitHub;
};

const ProjectCreationStep = () => {
  const [selectedProvider, setSelectedProvider] = useState('GitHub');

  const providers = [
    { name: 'GitHub', icon: <GitHubIcon />, color: '#24292e' },
    { name: 'GitLab', icon: <FaGitlab />, color: '#fc6d26' },
    { name: 'Bitbucket', icon: <FaBitbucket />, color: '#0052cc' },
    { name: 'Azure', icon: <IntegrationInstructionsIcon sx={{ fontSize: 16 }} />, color: '#0078d4' },
    { name: 'OpenAI', icon: <Target size={16} />, color: '#10a37f' },
    { name: 'Gemini', icon: <FaStar />, color: '#4285f4' },
    { name: 'Claude', icon: <Target size={16} />, color: '#d97706' },
    { name: 'JIRA', icon: <FaJira />, color: '#0052cc' },
    { name: 'Trello', icon: <IntegrationInstructionsIcon sx={{ fontSize: 16 }} />, color: '#0079bf' },
    { name: 'Zoho Sprints', icon: <FaTicketAlt />, color: '#e65100' }
  ];

  const currentInstructions = getProviderInstructions(selectedProvider);

  return (
    <>
      <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontSize: '2.25rem', lineHeight: '2.5rem' }}>Step 2: Create a Project</Typography>
      <Typography variant="body1" color="#6b7280" gutterBottom sx={{ mb: 4, fontSize: '1.25rem', lineHeight: '1.75rem' }}>
        Connect your repository and add authentication tokens.
      </Typography>

      {/* Project Configuration Section */}
      <Box sx={{ mb: 4, bgcolor: '#fff', border: '0 solid #e2e8f0', p: 6, borderRadius: '12px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
            Project Configuration
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
          Set up your project with the required information and authentication tokens.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              <Typography variant="h6" fontWeight={500} sx={{ color: '#1f1f1f', fontSize: '1rem' }}>
                {"Project Name"}
              </Typography>
              <TextField
                fullWidth
                placeholder="My Awesome Project"
                helperText="Used to identify your project in the dashboard."
                variant="outlined"
                size="small"
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              <Typography variant="h6" fontWeight={500} sx={{ color: '#1f1f1f', fontSize: '1rem' }}>
                {"User Emails"}
              </Typography>
              <TextField
                fullWidth
                placeholder="dev1@company.com, dev2@company.com"
                helperText="Add team members who should have access. Required for user access control and notifications."
                variant="outlined"
                size="small"
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Provider Integration Section */}
      <Box mb={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
            Provider Integration
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, mb: 3 }}>
          {providers.map((provider) => (
            <Chip
              key={provider.name}
              icon={provider.icon}
              label={provider.name}
              onClick={() => setSelectedProvider(provider.name)}
              variant={selectedProvider === provider.name ? 'filled' : 'outlined'}
              sx={{
                bgcolor: selectedProvider === provider.name ? '#fff' : 'transparent',
                color: selectedProvider === provider.name ? '#000000' : '#6b7280',
                border: '0 solid #e2e8f0',
                px: 4,
                borderRadius: '12px',
                '&:hover': {
                  bgcolor: selectedProvider === provider.name ? '#fff' : '#f3f4f6',
                }
              }}
            />
          ))}
        </Box>

        {/* Dynamic Provider Instructions */}
        <Box sx={{ bgcolor: 'white', border: '0 solid #e2e8f0', p: 6, borderRadius: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            {selectedProvider === 'GitHub' && <GitHubIcon sx={{ color: '#24292e', fontSize: 24 }} />}
            {selectedProvider === 'GitLab' && <FaGitlab size={24} color="#fc6d26" />}
            {selectedProvider === 'Bitbucket' && <FaBitbucket size={24} color="#0052cc" />}
            {selectedProvider === 'Azure' && <IntegrationInstructionsIcon sx={{ color: '#0078d4', fontSize: 24 }} />}
            {selectedProvider === 'OpenAI' && <Target size={20} />}
            {selectedProvider === 'Gemini' && <FaStar sx={{ color: '#4285f4', fontSize: 24 }} />}
            {selectedProvider === 'Claude' && <Target size={20} />}
            {selectedProvider === 'JIRA' && <FaJira sx={{ color: '#0052cc', fontSize: 24 }} />}
            {selectedProvider === 'Trello' && <IntegrationInstructionsIcon sx={{ color: '#0079bf', fontSize: 24 }} />}
            {selectedProvider === 'Zoho Sprints' && <FaTicketAlt size={24} color="#e65100" />}
            <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
              {currentInstructions.title}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            {currentInstructions.steps.map((step, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
                {step.startsWith('•') ? (
                  <>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6b7280', mt: 1.5, flexShrink: 0 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {step.substring(2).split('**').map((part, i) =>
                        i % 2 === 1 ? (
                          <Typography key={i} component="span" variant="body2" fontWeight="bold" color="text.secondary">
                            {part}
                          </Typography>
                        ) : part
                      )}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Box sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: '#3b82f6',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: 600,
                      flexShrink: 0,
                      mt: 0.5
                    }}>
                      {index + 1}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {step.split('\n').map((line, lineIndex) => (
                        <Box key={lineIndex} sx={{ mb: lineIndex < step.split('\n').length - 1 ? 0.5 : 0 }}>
                          {line.startsWith('•') ? (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6b7280', mt: 1.5, flexShrink: 0 }} />
                              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                {line.substring(2).split('**').map((part, i) =>
                                  i % 2 === 1 ? (
                                    <Typography key={i} component="span" variant="body2" fontWeight="bold" color="text.secondary">
                                      {part}
                                    </Typography>
                                  ) : part
                                )}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                              {line.split('**').map((part, i) =>
                                i % 2 === 1 ? (
                                  <Typography key={i} component="span" variant="body2" fontWeight="bold" color="text.secondary">
                                    {part}
                                  </Typography>
                                ) : part
                              )}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Typography>
                  </>
                )}
              </Box>
            ))}
           </Box>

           {/* Alert Boxes */}
          {currentInstructions.alerts.map((alert, index) => (
            <Alert
              key={index}
              severity={alert.type === 'warning' ? 'warning' : 'info'}
              sx={{
                mb: 3,
                bgcolor: alert.type === 'warning' ? '#fef8e9' : '#ebf2fe',
                color: '#000',
                border: 'none',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {alert.title}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {alert.message}
              </Typography>
            </Alert>
          ))}

          {/* Advanced Configuration */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BookOpen color='#737373' size={16} />
              <Typography variant="body2" fontWeight={600} sx={{ color: '#6b7280' }}>
                Advanced Configuration
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ color: '#6b7280', fontSize: 18 }} />
          </Box>
        </Box>
      </Box>

      {/* Review Comment Mode Section */}
      <Card sx={{ bgcolor: '#fff', border: '0 solid #e2e8f0', p: 4, borderRadius: '12px' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#1f1f1f' }}>
              Review Comment Mode
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
            Choose how AI feedback is delivered to your pull requests.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'start',
                  justifyContent: 'flex-start',
                  gap: 3,
                  border: '0 solid #e2e8f0',
                  p: 4,
                  borderRadius: '12px'
                }}
              >
                <Box sx={{
                  width: { xs: 22, sm: 30 },
                  height: { xs: 22, sm: 30 },
                  borderRadius: '50%',
                  bgcolor: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Zap color='#3b82f6' size={16} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'start', flexDirection: 'column', justifyContent: 'flex-start', gap: 1, mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#1f1f1f' }}>
                    PR Comment (single summary)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Posts a single summary comment on the PR. Best for high-level feedback.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'start',
                  justifyContent: 'flex-start',
                  gap: 3,
                  border: '0 solid #e2e8f0',
                  p: 4,
                  borderRadius: '12px'
                }}
              >
                <Box sx={{
                  width: { xs: 22, sm: 30 },
                  height: { xs: 22, sm: 30 },
                  borderRadius: '50%',
                  bgcolor: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <ExternalLink color='#10b981' size={16} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'start', flexDirection: 'column', justifyContent: 'flex-start', gap: 1, mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#1f1f1f' }}>
                    Inline
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Posts comments directly on changed lines. Best for detailed, line-by-line review.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Alert severity="info"
            sx={{
              bgcolor: '#ebf2fe',
              color: '#000',
              border: 'none',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              mb: 2,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                Bitbucket Note
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Bitbucket projects only support Inline mode due to API comment size limits.
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Choose the mode that matches your team's workflow. Inline is most actionable, PR Comment is concise and comprehensive.
          </Typography>
        </CardContent>
      </Card>
    </>
  );
};

export default ProjectCreationStep;